import { useApolloClient } from '@apollo/client'

import { GetDebitCardTransactions } from 'graphql/merchant-portal/queries/debitCardTransactions'
import { rvUpdatedDebitCardTransactions } from 'graphql/reactiveVariables'
import { DebitCardTransactionUpdate_onDebitCardTransactionUpdate as DebitCardTransactionUpdate } from 'types/gql-types/DebitCardTransactionUpdate'
import {
  GetDebitCardTransactions as GetDebitCardTransactionsResponse,
  GetDebitCardTransactions_getDebitCardTransactionsV2_transactions as DebitCardTransaction,
  GetDebitCardTransactionsVariables,
} from 'types/gql-types/GetDebitCardTransactions'

import {
  getIsADebitCardTransaction,
  padWithNulls,
} from './useUpdateDebitCardTransaction.utils'

interface UseUpdateDebitCardTransactionProps {
  variables: GetDebitCardTransactionsVariables
}

export const useUpdateDebitCardTransaction = ({
  variables,
}: UseUpdateDebitCardTransactionProps) => {
  const { cache } = useApolloClient()

  const updateTransaction = (
    update: DebitCardTransaction | DebitCardTransactionUpdate
  ) => {
    const updatedDebitCardTransactions = rvUpdatedDebitCardTransactions()

    const updatedDebitCardTransactionsWithoutUpdate =
      updatedDebitCardTransactions.filter(
        (optimisticTransaction) => optimisticTransaction.id !== update.id
      )

    const cachedTransactions = cache.readQuery<
      GetDebitCardTransactionsResponse,
      GetDebitCardTransactionsVariables
    >({
      query: GetDebitCardTransactions,
      variables,
    })

    const existingOptimisticTransaction = updatedDebitCardTransactions.find(
      (optimisticTransaction) => optimisticTransaction.id === update.id
    )

    const existingCachedTransaction =
      cachedTransactions?.getDebitCardTransactionsV2?.transactions?.find(
        (cachedTransaction) => cachedTransaction?.id === update.id
      )

    const existingTransaction =
      existingOptimisticTransaction ?? existingCachedTransaction

    const newContact = update.contact
      ? { ...update.contact, category: null }
      : null

    const updatedContact = existingTransaction?.contact
      ? { ...existingTransaction.contact, ...update.contact }
      : newContact

    if (getIsADebitCardTransaction(update)) {
      rvUpdatedDebitCardTransactions([
        ...updatedDebitCardTransactionsWithoutUpdate,
        { ...update, contact: updatedContact },
      ])
      return
    }

    if (existingTransaction?.merchant) {
      const newTransaction: DebitCardTransaction = {
        ...update,
        merchant: { ...existingTransaction?.merchant, ...update.merchant },
        contact: updatedContact,
        __typename: 'DebitCardTransactionV2',
      }

      rvUpdatedDebitCardTransactions([
        ...updatedDebitCardTransactionsWithoutUpdate,
        newTransaction,
      ])
      return
    }

    const merchant = update.merchant ? padWithNulls(update.merchant) : null

    const updatedTransaction: DebitCardTransaction = {
      ...update,
      merchant,
      contact: updatedContact,
      __typename: 'DebitCardTransactionV2',
    }

    rvUpdatedDebitCardTransactions([
      ...updatedDebitCardTransactionsWithoutUpdate,
      updatedTransaction,
    ])
  }

  return { updateTransaction }
}
